import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { ApiErrorCode, ApiErrorResponse } from '@sorrem/shared';

/**
 * Every failed API request, as the store's base query hands it on (#329):
 * the HTTP status (or fetch's own failure kind) and the shared error body.
 * A failure without one (a network error, an HTML 500) carries the code its
 * status implies and no player-facing reason.
 */
export interface ApiFailure {
    readonly status: FetchBaseQueryError['status'];
    readonly data: ApiErrorResponse;
}

/** The code a status implies when the server sent no error body. */
function codeForStatus(status: FetchBaseQueryError['status']): ApiErrorCode {
    switch (status) {
        case 400: return 'invalid-request';
        case 401: return 'unauthorized';
        case 403: return 'forbidden';
        case 404: return 'not-found';
        case 409: return 'conflict';
        case 429: return 'rate-limited';
        case 'FETCH_ERROR':
        case 'TIMEOUT_ERROR':
        case 503: return 'service-unavailable';
        default: return 'internal-error';
    }
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
    const body = value as Partial<ApiErrorResponse> | null;
    return typeof body === 'object' && body !== null && typeof body.error === 'string' && typeof body.code === 'string';
}

/** Normalizes a fetch base query failure into an `ApiFailure`. */
export function toApiFailure(error: FetchBaseQueryError): ApiFailure {
    const body = 'data' in error ? error.data : undefined;
    return {
        status: error.status,
        data: isApiErrorResponse(body) ? body : { error: '', code: codeForStatus(error.status) },
    };
}

/** Whether a caught value is a failed API request (what `.unwrap()` throws). */
export function isApiFailure(value: unknown): value is ApiFailure {
    const failure = value as Partial<ApiFailure> | null;
    return typeof failure === 'object' && failure !== null && 'status' in failure && isApiErrorResponse(failure.data);
}

/**
 * What to tell the player about a failed request: the server's own reason,
 * or `fallback` when there is none (a network failure, a crash).
 */
export function resolveApiErrorMessage(failure: unknown, fallback: string): string {
    return isApiFailure(failure) && failure.data.error !== '' ? failure.data.error : fallback;
}
